/**
 * The projects tab: the list of projects, or one project's page.
 *
 * ── WHICH ONE IS SHOWING IS URL STATE, NOT COMPONENT STATE ─────────────────
 *
 * `?project=` decides, and `?sub=` decides which panel of it is open. This app
 * has no route table - `main.tsx` is a `location.pathname` if-chain and
 * everything inside the hub hangs off `?tab=` - so a project page is not a new
 * route. It is a second thing the projects tab can be, chosen the same way every
 * other screen here is chosen: by reading the query string on every render.
 *
 * The predecessor kept it in `useState` seeded from a prop, which cost a real
 * bug: the operator's "view as customer" link landed on a project, and there was
 * no way to link to a panel of it, no way to reload back into it, and the
 * browser's Back button walked out of the hub entirely rather than back to the
 * list. Reading the URL instead makes all three fall out for free.
 *
 * ── EVERY NAVIGATION GOES THROUGH `keepPreview` ────────────────────────────
 *
 * `setParams` REPLACES the whole query string. During a staff preview that means
 * the first click would drop the token, and the portal would empty itself
 * mid-preview - which reads as a customer with no records rather than as a link
 * that expired. `keepPreview` carries the token and nothing else, so an ordinary
 * visit is unchanged. Note what it does NOT carry: which project is open. That
 * is this file's business now, written into the URL like any other visitor's.
 */
import { useSearchParams } from "react-router-dom";
import { keepPreview } from "../../../lib/previewMode";
import { ProjectList } from "./ProjectList";
import { ProjectPage } from "./ProjectPage";

export function ProjectsSection() {
  const [params, setParams] = useSearchParams();
  const projectId = params.get("project");

  function open(id: string, sub?: string) {
    setParams(keepPreview({ tab: "projects", project: id, ...(sub ? { sub } : {}) }));
  }

  // Back drops `project` AND `sub` by rewriting the query without them. Leaving
  // a stale `sub` behind would decide which panel the NEXT project opens on,
  // which is a panel that project may not even have granted.
  function back() {
    setParams(keepPreview({ tab: "projects" }));
  }

  if (projectId) {
    return (
      <ProjectPage
        // Keyed by id so switching projects remounts rather than showing the
        // previous project's panels under the new project's name for a frame.
        key={projectId}
        id={projectId}
        requestedSub={params.get("sub")}
        onBack={back}
        onSelectTab={(sub) => open(projectId, sub)}
      />
    );
  }

  return <ProjectList onOpen={(id) => open(id)} />;
}
